// Tenantra Inc. - Property Verification Page (Microsoft 365 Style - Compact)
import { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, Clock,
  X, Image, File, Loader2, Shield, Building2, MapPin,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';

// Status configurations
const STATUS_CONFIG = {
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  pending: { label: 'Pending Review', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', icon: Clock },
  incomplete: { label: 'Not Started', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  needs_documents: { label: 'Needs Documents', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  manual_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle }
};

// Compact file upload component
function FileUploadZone({ onFileSelect, selectedFile, accept, disabled }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) onFileSelect(file);
  };

  const getFileIcon = () => {
    if (!selectedFile) return Upload;
    if (selectedFile.type.startsWith('image/')) return Image;
    return File;
  };

  const FileIcon = getFileIcon();

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-lg p-5 text-center transition-all cursor-pointer ${
        disabled 
          ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-50' 
          : isDragging 
            ? 'border-teal-500 bg-teal-50' 
            : selectedFile 
              ? 'border-emerald-400 bg-emerald-50' 
              : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
      />
      
      <div className={`w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center ${
        selectedFile ? 'bg-emerald-100' : 'bg-slate-100'
      }`}>
        <FileIcon className={`w-5 h-5 ${selectedFile ? 'text-emerald-600' : 'text-slate-400'}`} />
      </div>
      
      {selectedFile ? (
        <>
          <p className="text-slate-800 text-sm font-medium">{selectedFile.name}</p>
          <p className="text-slate-500 text-xs mt-0.5">
            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
          <button
            type="button"
            className="mt-2 text-xs text-slate-500 hover:text-red-500 inline-flex items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onFileSelect(null);
            }}
          >
            <X className="w-3 h-3" />
            Remove
          </button>
        </>
      ) : (
        <>
          <p className="text-slate-700 text-sm font-medium">Upload Document</p>
          <p className="text-slate-500 text-xs mt-0.5">PDF, JPG, PNG or WEBP up to 15MB</p>
          <p className="text-xs text-slate-400 mt-1">Click or drag & drop</p>
        </>
      )}
    </div>
  );
}

export default function PropertyVerifyPage() {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);

  // Get property ID from URL
  const pathParts = window.location.pathname.split('/');
  const actualPropertyId = pathParts[pathParts.indexOf('properties') + 1];

  useEffect(() => {
    if (actualPropertyId && actualPropertyId !== 'new') {
      fetchProperty();
    }
  }, [actualPropertyId]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/properties/${actualPropertyId}`);
      setProperty(response.data);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
      navigate('/landlord/properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    if (!documentFile) {
      toast.error('Please select a document to upload');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('document', documentFile);

      const response = await api.post(
        `/verification/property/${actualPropertyId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setVerificationResult(response.data);
      
      if (response.data.status === 'verified') {
        toast.success('Property verified successfully!');
      } else if (response.data.status === 'manual_review') {
        toast.info('Document submitted for manual review');
      } else {
        toast.warning(response.data.message || 'Verification needs attention');
      }

      fetchProperty();
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <h3 className="text-base font-semibold text-slate-800 mb-2">Property Not Found</h3>
        <a href="/landlord/properties">
          <Button className="h-8 text-sm bg-teal-600 hover:bg-teal-700 border-0">
            Back to Properties
          </Button>
        </a>
      </div>
    );
  }

  const status = STATUS_CONFIG[property.verification_status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const isVerified = property.verification_status === 'verified';
  const underReview = property.verification_status === 'manual_review';
  const review = verificationResult?.review || property.ownership_review;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header - Compact */}
      <div>
        <a 
          href="/landlord/properties" 
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Properties
        </a>
        <h1 className="text-lg font-semibold text-slate-800">Property Verification</h1>
        <p className="text-sm text-slate-500">Upload ownership documents to verify your property</p>
      </div>

      {/* Property Card - Compact */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                {property.address.street}
                {property.address.unit_number && (
                  <span className="text-slate-500 font-normal"> #{property.address.unit_number}</span>
                )}
              </h2>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {property.address.city}, {property.address.province} {property.address.postal_code}
              </p>
            </div>
          </div>
          <Badge className={`${status.color} text-xs px-2 py-0.5`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {status.label}
          </Badge>
        </div>
      </div>

      {/* Verification Status */}
      {isVerified ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-emerald-800">Property verified</h3>
              <p className="text-xs text-emerald-700">Your property ownership has been confirmed. You can now report tenant behaviour.</p>
              {property.verified_at && (
                <p className="text-xs text-emerald-600 mt-1">
                  Verified on {formatDate(property.verified_at)}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-emerald-200 flex gap-2">
            <a href="/landlord/reports/new">
              <Button className="h-8 text-sm bg-teal-600 hover:bg-teal-700 border-0">
                Submit Tenant Report
              </Button>
            </a>
            <a href="/landlord/properties">
              <Button variant="outline" className="h-8 text-sm border-slate-200 text-slate-600">
                View All Properties
              </Button>
            </a>
          </div>
        </div>
      ) : underReview ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="property-under-review">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Document under review</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Your {review?.document_subtype ? review.document_subtype.toLowerCase().replace(/_/g, ' ') : 'document'} was received{review?.checked_at ? ` on ${formatDate(review.checked_at)}` : ''}. Our team reviews flagged documents within 1-2 business days and will email you the outcome.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {property.verification_status === 'rejected' && property.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="property-rejection-notice">
              <p className="text-sm font-semibold text-red-800">Previous document not accepted</p>
              <p className="text-xs text-red-700 mt-0.5">{property.rejection_reason}</p>
              <p className="text-xs text-red-700 mt-1">Upload a recent document in your name that shows this property address.</p>
            </div>
          )}
          {/* Upload Section - Compact */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-teal-500" />
              <span className="text-sm font-medium text-slate-700">Upload Ownership Document</span>
            </div>
            
            {/* Document types - Compact inline badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Property Tax Bill', 'Title / Land Registry', 'Mortgage Statement', 'Home Insurance Policy'].map((doc, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600">
                  <FileText className="w-3 h-3 text-slate-400" />
                  {doc}
                </span>
              ))}
            </div>

            <FileUploadZone
              onFileSelect={setDocumentFile}
              selectedFile={documentFile}
              accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
              disabled={submitting}
            />

            {/* Verification Result */}
            {verificationResult && (
              <div className={`mt-4 p-3 rounded-lg border text-sm ${
                verificationResult.status === 'verified' 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                  : verificationResult.status === 'manual_review'
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-amber-50 border-amber-200 text-amber-700'
              }`}>
                <p className="font-medium" data-testid="property-verification-message">{verificationResult.message}</p>
                {verificationResult.review?.document_subtype && (
                  <p className="text-xs mt-1 opacity-75 capitalize">
                    Detected: {verificationResult.review.document_subtype.toLowerCase().replace(/_/g, ' ')}{verificationResult.review.issuer ? ` from ${verificationResult.review.issuer}` : ''}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              <a href="/landlord/properties">
                <Button type="button" variant="outline" className="h-8 px-3 text-sm border-slate-200 text-slate-600">
                  Cancel
                </Button>
              </a>
              <Button
                onClick={handleSubmitVerification}
                data-testid="property-verify-submit-btn"
                disabled={!documentFile || submitting}
                className="h-8 px-4 text-sm bg-teal-600 hover:bg-teal-700 border-0"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Checking document…</>
                ) : (
                  <>
                    <Shield className="w-3.5 h-3.5 mr-1.5" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Info Banner - Compact */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-start gap-2.5">
              <Shield className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-slate-700 font-medium">Why Verification?</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Each document is checked for tampering and matched against your verified identity and this property's address. Clean documents are verified immediately; anything flagged is reviewed by our team.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
